import React, { Fragment, useId, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PlusOutlined } from '@ant-design/icons'

import { ExtendedTable, SubheaderToolbarDivider } from '@app/components'
import { TableSearchButton } from '@app/components/TableSettingsToolbar'
import { LntButton, LntDrawer, LntForm, LntFormItem, LntInput } from '@app/components/base'
import { AddAbnormalPriceDetectionDrawer } from '@app/drawers/AddAbnormalPriceDetectionDrawer'

import { MONITORING_VALIDATION_KEY } from '@app/constants/queryKeys'
import { BUTTON_TYPE, SIZE } from '@app/constants'

import { useNotification, usePaginationParams, useRemoveTableRowButton, useToggle } from '@app/hooks'

import {
  deleteMonitoringValidationConfigSpecialCases,
  getMonitoringValidationConfig,
  getMonitoringValidationConfigSpecialCases,
  patchMonitoringValidationConfig
} from '@app/api/routes/monitoringValidation'

import { isEmptyObject } from '@app/utils/object'
import { getMonitoringValidationConfigSpecialCasesFromResponse } from '@app/utils/responseAdapters'

import { getColumns } from './utils'

import styles from './styles.module.scss'

import type { AbnormalPriceDetectionDrawerProps } from './types'
import type { PatchMonitoringValidationConfigData } from '@app/api/routes/monitoringValidation/types'
import type { MonitoringValidationConfigSpecialCasesDataSource } from '@app/types/dataSource'

const columns = getColumns()

const title = 'Выявление аномальных цен'

export const AbnormalPriceDetectionDrawer: React.FC<AbnormalPriceDetectionDrawerProps> = props => {
  const { onClose } = props

  const {
    value: isSpecialCasesDrawerOpen,
    setUntoggled: handleCloseSpecialCasesDrawer,
    setToggled: handleOpenSpecialCasesDrawer
  } = useToggle()

  const { pagination, setPagination, paginationParams } = usePaginationParams()
  const formId = useId()
  const form = useForm<PatchMonitoringValidationConfigData>()

  const queryClient = useQueryClient()
  const notification = useNotification()

  const { data: configData } = useQuery({
    queryKey: [MONITORING_VALIDATION_KEY.config],
    queryFn: async () => await getMonitoringValidationConfig()
  })

  const { data: monitoringValidationSpecialCasesData, isLoading: isMonitoringValidationSpecialCasesDataLoading } =
    useQuery({
      queryKey: [MONITORING_VALIDATION_KEY.specialCases],
      queryFn: async () => await getMonitoringValidationConfigSpecialCases({ ...paginationParams })
    })

  const patchConfigDataMutation = useMutation({
    mutationFn: patchMonitoringValidationConfig,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_VALIDATION_KEY.config] })

      notification.success({
        message: 'Успешно изменен период обращения в историю.'
      })

      onClose?.()
    }
  })

  const deleteMonitoringValidationConfigSpecialCasesMutation = useMutation({
    mutationFn: deleteMonitoringValidationConfigSpecialCases,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORING_VALIDATION_KEY.specialCases] })

      notification.success({
        message: 'Выбранные кластеры удалены из транслирования.'
      })

      onClose?.()
    }
  })

  const dataSource = useMemo(
    () => getMonitoringValidationConfigSpecialCasesFromResponse(monitoringValidationSpecialCasesData),
    [monitoringValidationSpecialCasesData]
  )

  const handleSubmit = (): void => {
    const { historicalDepth } = form.getValues()

    if (historicalDepth) {
      patchConfigDataMutation.mutate({ historicalDepth })
    }
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleDeleteSpecialCases = (id: string): void => {
    deleteMonitoringValidationConfigSpecialCasesMutation.mutate({ id })
  }

  useRemoveTableRowButton({
    columns,
    onClick: rowKey => handleDeleteSpecialCases(String(rowKey))
  })

  const footerButtons = (
    <div className={ styles.abnormalPriceDetectionDrawer__footer }>
      <LntButton
        htmlType='submit'
        form={ formId }
        disabled={ isEmptyObject(form.formState.dirtyFields) }
      >
        Сохранить
      </LntButton>
    </div>
  )

  const toolbar = [
    <Fragment key='add-competitor'>
      <LntButton
        icon={ <PlusOutlined /> }
        type={ BUTTON_TYPE.primary }
        size={ SIZE.small }
        onClick={ handleOpenSpecialCasesDrawer }
      >
        Исключение
      </LntButton>

      <SubheaderToolbarDivider />
    </Fragment>,

    // TODO: Добавить табличный фильтр
    <TableSearchButton key='search-competitors' />
  ]

  return (
    <LntDrawer
      { ...props }
      title={ title }
      width={ 1080 }
      footer={ footerButtons }
    >
      <LntForm
        layout='horizontal'
        formInstance={ form }
        id={ formId }
        onSubmit={ handleSubmit }
      >
        <div className={ styles.abnormalPriceDetectionDrawer__formTop }>
          <LntFormItem
            name='historicalDepth'
            defaultValue={ configData?.historicalDepth }
            label='Период обращения в историю цен'
          >
            <LntInput
              className={ styles.abnormalPriceDetectionDrawerFormTop__inputDays }
              placeholder='Кол-во дней'
              type='number'
            />
          </LntFormItem>
        </div>

        <ExtendedTable<MonitoringValidationConfigSpecialCasesDataSource>
          loading={ isMonitoringValidationSpecialCasesDataLoading }
          toolbar={ toolbar }
          columns={ columns }
          dataSource={ dataSource }
          pagination={ {
            ...pagination,
            total: monitoringValidationSpecialCasesData?.count,
            onChange: handlePaginationChange
          } }
        />
      </LntForm>

      <AddAbnormalPriceDetectionDrawer
        open={ isSpecialCasesDrawerOpen }
        onClose={ handleCloseSpecialCasesDrawer }
      />
    </LntDrawer>
  )
}
